import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Upload } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { WalletDesign } from "./WalletDesign";

const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

const Status = ({ s }) => {
  const { t } = useLang();
  if (!s.configured) return <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600" data-testid="wallet-status-off">{t("Apple Wallet is off. Upload the certificate below to turn it on.")}</p>;
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="wallet-status-on">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="space-y-0.5">
        <p className="font-semibold">{t("Apple Wallet is on")}</p>
        <p dir="ltr" className="text-start font-mono text-xs">{s.pass_type_id} · {s.team_id}</p>
        <p className="text-xs">{s.cert_name} · {t("Certificate expires {date}", { date: s.cert_expires })}</p>
        {s.source === "server" && <p className="text-xs">{t("Using the certificate saved on the server.")}</p>}
      </div>
    </div>
  );
};

export const WalletSettingsPanel = () => {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ password: "", pass_type_id: "", team_id: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/wallet").then(({ data }) => setStatus(data)).catch((e) => toast.error(errorMessage(e))); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = new FormData();
    body.append("certificate", file);
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    try {
      const { data } = await api.put("/admin/settings/wallet", body);
      setStatus(data);
      setForm({ password: "", pass_type_id: "", team_id: "" });
      setFile(null);
      toast.success(t("Wallet settings saved"));
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("Remove the Wallet certificate? Employees won't be able to add new passes."))) return;
    try { const { data } = await api.delete("/admin/settings/wallet"); setStatus(data); toast.success(t("Wallet certificate removed")); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  if (!status) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="space-y-4">
    <form onSubmit={save} className="max-w-2xl space-y-5 rounded-3xl border border-slate-200/80 bg-white p-6" data-testid="wallet-settings-panel">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white"><Wallet className="h-5 w-5" /></span>
        <h2 className="font-display text-xl font-bold text-slate-900">{t("Wallet settings")}</h2>
      </div>
      <Status s={status} />
      <p className="text-sm text-slate-500">{t("From your Apple Developer account: create a Pass Type ID, download its certificate, export it as a .p12 file with a password, then upload it here.")}</p>
      <div className="space-y-1.5">
        <Label className={labelCls}>{t("Certificate (.p12)")} *</Label>
        <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 text-sm text-slate-600 hover:border-slate-900">
          <Upload className="h-4 w-4" /> <span className="truncate">{file ? file.name : t("Choose file")}</span>
          <input type="file" accept=".p12,application/x-pkcs12" required className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="wallet-cert-input" />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Certificate password")} *</Label>
          <Input type="password" dir="ltr" required value={form.password} onChange={set("password")} autoComplete="off" className="h-12 rounded-xl" data-testid="wallet-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Pass Type ID")} *</Label>
          <Input dir="ltr" required value={form.pass_type_id} onChange={set("pass_type_id")} placeholder="pass.sa.inception.card" className="h-12 rounded-xl font-mono text-sm" data-testid="wallet-pass-type-input" />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>{t("Team ID")} *</Label>
          <Input dir="ltr" required maxLength={10} value={form.team_id} onChange={set("team_id")} placeholder="ABCDE12345" className="h-12 rounded-xl font-mono text-sm uppercase" data-testid="wallet-team-input" />
        </div>
      </div>
      <p className="text-xs text-slate-400">{t("The certificate and password are stored encrypted and are never shown again.")}</p>
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={saving} data-testid="wallet-save-button"
          className="btn-press min-h-[50px] rounded-2xl bg-slate-900 px-6 font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : status.configured ? t("Replace certificate") : t("Turn on Apple Wallet")}
        </button>
        {status.source === "settings" && <button type="button" onClick={remove} data-testid="wallet-remove-button"
          className="btn-press min-h-[50px] rounded-2xl px-5 font-semibold text-slate-500 hover:text-wade">{t("Remove certificate")}</button>}
      </div>
    </form>
    <WalletDesign />
    </div>
  );
};
